import { type FormEvent, useEffect, useState } from 'react';
import { toast } from '@/lib/notify';

import { cn } from '@/lib/utils';

import { useVerifyStep } from '../../api/useVerifyStep';
import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { CHECK_FAILED_TOAST } from '../stepFallbackNotes';
import { PanCardMock } from './PanCardMock';
import { PanResultBlock } from './PanResultBlock';
import { type ActiveCardField, extractError, findKey, formatDob, parsedInput } from './pan.utils';

export function PanFormContent({
  session,
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);
  const saved = parsedInput(step.inputJson);
  const [pan, setPan] = useState('');
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeField, setActiveField] = useState<ActiveCardField>(null);

  const lastError = extractError(step.resultJson);
  const showResult =
    step.status === 'Passed' || step.status === 'Review' || step.status === 'Failed';
  const readOnly = showResult;
  const panOk = pan.trim().length > 0;
  const nameOk = name.trim().length > 0;
  const isReady = panOk && nameOk && consent;

  useEffect(() => {
    onPendingChange(isPending);
  }, [isPending, onPendingChange]);
  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady, onReadyChange]);

  let fatherName: string | undefined;
  if (readOnly && step.resultJson) {
    try {
      const r = JSON.parse(step.resultJson);
      const f =
        findKey(r, 'father_name') ??
        findKey(r, 'fathers_name') ??
        findKey(r, 'father_name_as_per_pan');
      if (f !== undefined) fatherName = String(f);
    } catch {
      /* ignore */
    }
  }

  function handleVerify(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!isReady) return;
    mutate(
      {
        stepId: step.id,
        input: {
          consent: 'Y',
          consent_text:
            'I hear by declare my consent agreement for fetching my information via SignCare API',
          request_id: crypto.randomUUID(),
          pan_number: pan.trim().toUpperCase(),
          pan_holder_name: name.trim(),
          ...(dob ? { dob: formatDob(dob) } : {}),
        },
      },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          if (updated?.status === 'Passed') {
            toast.success('PAN verified successfully');
          } else if (updated?.status === 'Review') {
            toast.warning('PAN verified — flagged for review');
          } else {
            toast.error(CHECK_FAILED_TOAST);
          }
        },
        onError: () => toast.error('Could not reach the server. Please try again.'),
      },
    );
  }

  // Matches the designer's `.sc-workflow-preview__input` exactly: px-3.5 / py-2.5 and
  // a 13px value. The 13px is the reason their form fits a phone where ours didn't —
  // it is one step down from the 14px label above it, which reads as deliberate
  // hierarchy rather than as small text.
  const inputBase =
    'w-full rounded-lg border px-3.5 py-2.5 text-[13px] text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:ring-1';
  const inputOk = 'border-[#E5E7EB] focus:border-[#0048FF] focus:ring-[#0048FF]';
  const inputErr = 'border-[#DC2626] focus:border-[#DC2626] focus:ring-[#DC2626]';

  return (
    // Vertical rhythm taken from the designer's own preview CSS: their
    // `.sc-workflow-preview__form` is `gap-1` with each `__field` carrying `mb-1.5`,
    // i.e. ~10px between fields — where ours was `gap-4` (16px). Across a heading,
    // a card mock and three fields that difference alone was most of the reason the
    // screen overflowed a phone. `gap-2.5` reproduces their 10px in one place
    // instead of splitting it between a gap and a margin.
    <form id={formId} onSubmit={handleVerify} noValidate className="flex flex-col gap-2.5">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-[#111827]">PAN Verification</h2>
        <p className="mt-0.5 text-[12px] text-[#6B7280]">Please enter your PAN Details</p>
      </div>

      <div className="w-full">
        <PanCardMock
          panNumber={readOnly ? saved.pan_number : pan || undefined}
          name={readOnly ? saved.pan_holder_name : name || undefined}
          fatherName={fatherName}
          dob={readOnly ? saved.dob : dob ? formatDob(dob) : undefined}
          activeField={readOnly ? null : activeField}
        />
      </div>

      {/* PAN Number */}
      <div className="flex flex-col gap-1">
        {/* htmlFor/id pairs on all three fields: tapping the label focuses the input,
            which matters more on a phone than on a mouse, and it's what a screen
            reader needs to announce the field. */}
        <label htmlFor={`${formId}-pan`} className="text-sm font-medium text-[#111827]">
          PAN Number{!readOnly && <span className="text-[#DC2626]">*</span>}
        </label>
        {readOnly ? (
          <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5 text-sm font-medium uppercase tracking-wide text-[#111827]">
            {saved.pan_number || '—'}
          </div>
        ) : (
          <>
            <input
              id={`${formId}-pan`}
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase())}
              onFocus={() => setActiveField('pan')}
              onBlur={() => setActiveField(null)}
              maxLength={10}
              placeholder="ABCDE1234F"
              className={cn(inputBase, 'uppercase', submitted && !panOk ? inputErr : inputOk)}
            />
            {submitted && !panOk && (
              <span className="text-xs text-[#DC2626]">PAN number is required</span>
            )}
          </>
        )}
      </div>

      {/* Full Name */}
      <div className="flex flex-col gap-1">
        <label htmlFor={`${formId}-name`} className="text-sm font-medium text-[#111827]">
          Full Name{!readOnly && <span className="text-[#DC2626]">*</span>}
        </label>
        {readOnly ? (
          <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5 text-sm text-[#111827]">
            {saved.pan_holder_name || '—'}
          </div>
        ) : (
          <>
            <input
              id={`${formId}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setActiveField('name')}
              onBlur={() => setActiveField(null)}
              placeholder="Smith Jhon"
              className={cn(inputBase, submitted && !nameOk ? inputErr : inputOk)}
            />
            {submitted && !nameOk && (
              <span className="text-xs text-[#DC2626]">Full name is required</span>
            )}
          </>
        )}
      </div>

      {/* Date of Birth */}
      <div className="flex flex-col gap-1">
        <label htmlFor={`${formId}-dob`} className="text-sm font-medium text-[#111827]">
          Date of Birth <span className="font-normal text-[#9CA3AF]">(Optional)</span>
        </label>
        {readOnly ? (
          <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5 text-sm text-[#111827]">
            {saved.dob || '—'}
          </div>
        ) : (
          <input
            id={`${formId}-dob`}
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            onFocus={() => setActiveField('dob')}
            onBlur={() => setActiveField(null)}
            max={new Date().toISOString().split('T')[0]}
            className={cn(
              inputBase,
              inputOk,
              '[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60',
            )}
          />
        )}
      </div>

      {/* Consent */}
      {!readOnly && (
        <div>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#0048FF]"
            />
            <span
              className={cn(
                'text-xs leading-snug',
                submitted && !consent ? 'text-[#DC2626]' : 'text-[#6B7280]',
              )}
            >
              I hear by declare my consent agreement for fetching my information via SignCare API
            </span>
          </label>
          {submitted && !consent && (
            <p className="mt-1 text-xs text-[#DC2626]">Consent is required to proceed</p>
          )}
        </div>
      )}

      {showResult && <PanResultBlock step={step} />}

      {lastError && !showResult && (
        <p className="text-xs text-[#BF3636]">Last attempt failed: {lastError}</p>
      )}

      {/* The reassurance line was dropped: the consent text directly above already
          says what happens to the data, and the "Secured by SignCare" footer carries
          the same message on every screen. Removing it was the last ~28px standing
          between this screen and fitting a small phone without scrolling. */}
    </form>
  );
}
